import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment';
import {
  CalendarDays,
  Send,
  X,
  Trophy,
  Clock,
  BarChart3,
  Trash2,
  ExternalLink,
  Loader2,
  Heart,
  Eye,
  MessageSquare,
  ChevronRight,
  RefreshCw,
  Flame,
  Share2
} from 'lucide-react';
import { getPlatformIcon as getPlatformIconUtil, getPlatformColor as getPlatformColorUtil } from '../../utils/platformIcons';
import { API_BASE_URL } from '../../config';
import { getPostText } from '../../utils/general';
import ScheduleCalendar from '../../components/ScheduleCalendar';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { usePostModal } from '../../contexts/PostModalContext';
import { cn } from "../../lib/utils";

import UniversalEmbedPlayer from '../../components/UniversalEmbedPlayer';
import { PublishStatusBadge } from '../../components/PublishStatusBadge';

interface PostSchedule {
  id: string;
  postId: string;
  socialAccountId: string;
  scheduledAt: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'DELETED';
  likes: number;
  comments: number;
  shares: number;
  views: number;
  bookmarks: number;
  isBanger: boolean;
  externalPostId?: string;
    post: {
    id: string;
    textEn?: string;
    textEs?: string;
    translations?: Record<string, { text: string, tiktokTitle?: string }>;
    game: { name: string };
    media: Array<{ filePath: string; thumbnailPath?: string | null; mediaType: string }>;
  };
  socialAccount: {
    platform: string;
    handle?: string;
    name: string;
  };
}

interface PostSession {
  id: string;
  textEn?: string;
  textEs?: string;
  translations?: Record<string, { text: string, tiktokTitle?: string }>;
  createdAt: string;
  game: { name: string };
  media: Array<{ filePath: string; thumbnailPath?: string | null; mediaType: string }>;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalViews: number;
  totalBookmarks: number;
    platforms: Array<{
      id: string;
      platform: string;
      handle?: string;
      accountName?: string;
      externalPostId?: string;
      externalPostUrl?: string;
      status?: string;
      scheduledAt: string;
      result?: string;
    }>;
  schedules: any[];
}

export default function PostsPage() {
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const { openPostById } = usePostModal();
  const [bestPeriod, setBestPeriod] = useState<'week' | 'month'>('week');

  // Queries
  const { data: scheduledPosts = [], isLoading: loadingScheduled } = useQuery<PostSchedule[]>({
    queryKey: ['scheduled-posts'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/posts/scheduled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  const { data: bestPosts = [], isLoading: loadingBest } = useQuery<PostSchedule[]>({
    queryKey: ['best-posts', bestPeriod],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/posts/best?period=${bestPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  const { data: allPosts = [], isLoading: loadingAll } = useQuery<PostSession[]>({
    queryKey: ['all-posts-table'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/posts/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both old array format and new object format
      let posts = [];
      let updatedStats = false;

      if (Array.isArray(response.data)) {
        posts = response.data;
      } else {
        posts = response.data.posts || [];
      }
      
      return posts.map((post: any) => {
        const schedules = post.schedules || [];
        const totalLikes = schedules.reduce((sum: number, s: any) => sum + (s.likes || 0), 0);
        const totalComments = schedules.reduce((sum: number, s: any) => sum + (s.comments || 0), 0);
        const totalShares = schedules.reduce((sum: number, s: any) => sum + (s.shares || 0), 0);
        const totalViews = schedules.reduce((sum: number, s: any) => sum + (s.views || 0), 0);
        const totalBookmarks = schedules.reduce((sum: number, s: any) => sum + (s.bookmarks || 0), 0);
        
        return {
          id: post.id,
          textEn: post.textEn,
          textEs: post.textEs,
          translations: post.translations,
          createdAt: post.createdAt,
          game: post.game,
          media: post.media,
          totalLikes,
          totalComments,
          totalShares,
          totalViews,
          totalBookmarks,
          platforms: (post.schedules || [])
            .map((s: any) => ({
              id: s.id,
              platform: s.socialAccount?.platform || 'unknown',
              handle: s.socialAccount?.handle || 'unknown',
              accountName: s.socialAccount?.name || s.socialAccount?.handle || '',
              externalPostId: s.externalPostId,
              externalPostUrl: s.externalPostUrl,
              status: s.status,
              scheduledAt: s.scheduledAt,
              result: s.result
            }))
            .sort((a: any, b: any) => a.platform.localeCompare(b.platform)),
          schedules: post.schedules
        };
      }).sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },
    enabled: !!token,
    refetchInterval: (query) => {
      // Poll every 3 seconds if any post is currently processing or was recently created
      // This ensures the UI stays in sync while background jobs are running.
      const data = query.state.data as PostSession[] | undefined;
      const tenMinutesAgo = Date.now() - (10 * 60 * 1000); // More aggressive polling for very recent posts
      const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
      
      const isActiveProcessing = data?.some((post: any) => {
        const postCreatedAt = new Date(post.createdAt).getTime();
        
        // 1. If it was created in the last 10 minutes, poll to see progress (even if still PENDING)
        if (postCreatedAt > tenMinutesAgo) {
          const hasIncompleteSchedules = post.platforms?.some((p: any) => 
            p.status === 'PROCESSING' || p.status === 'PENDING'
          );
          if (hasIncompleteSchedules) return true;
        }

        // 2. If it's older but still marks as PROCESSING, keep polling
        if (postCreatedAt > twoHoursAgo) {
          return post.platforms?.some((p: any) => p.status === 'PROCESSING');
        }

        return false;
      });

      return isActiveProcessing ? 3000 : 60000; // Poll every minute normally, every 3s if active
    }
  });

  // Mutations
  const cancelScheduleMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      await axios.delete(`${API_BASE_URL}/posts/schedule/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] });
      queryClient.invalidateQueries({ queryKey: ['all-posts-table'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
      toast.success('Schedule cancelled successfully');
    },
    onError: () => toast.error('Failed to cancel schedule')
  });

  const sendNowMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      await axios.post(`${API_BASE_URL}/posts/schedule/${scheduleId}/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] });
      queryClient.invalidateQueries({ queryKey: ['all-posts-table'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-summary'] });
      toast.success('Post triggered for immediate delivery');
    },
    onError: () => toast.error('Failed to send post now')
  });

  const getPlatformIcon = (platform: string, sizeClassName: string = 'h-3 w-3') => getPlatformIconUtil(platform, sizeClassName);
  const getPlatformColor = (platform: string) => getPlatformColorUtil(platform);

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <CalendarDays className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">Publishing Agenda</h1>
            <p className="text-muted-foreground font-medium">Posts Management</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Calendar & Best Performing (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          <ScheduleCalendar />
          
          <Card className="bg-card border-border shadow-xl overflow-hidden">
            <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
                  <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                    <Trophy className="h-4 w-4 text-primary" />
                  </div>
                  Best Performing
                </CardTitle>
                <div className="flex bg-background rounded-lg p-0.5 border border-border">
                  <button 
                    onClick={() => setBestPeriod('week')}
                    className={`px-2 py-1 text-[9px] font-black uppercase rounded-md transition-all ${bestPeriod === 'week' ? 'bg-primary text-white shadow-lg' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Week
                  </button>
                  <button 
                    onClick={() => setBestPeriod('month')}
                    className={`px-2 py-1 text-[9px] font-black uppercase rounded-md transition-all ${bestPeriod === 'month' ? 'bg-primary text-white shadow-lg' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Month
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-3">
              {loadingBest ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : bestPosts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-xs italic">Go, show your game to the world, champ!</div>
              ) : (
                bestPosts.map((s, idx) => (
                  <div key={s.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border group hover:border-primary/30 transition-all cursor-pointer" onClick={() => openPostById(s.post.id)}>
                    <div className="flex flex-col items-center justify-center h-12 w-12 bg-card rounded-lg text-amber-500 font-black italic relative overflow-hidden shrink-0">
                      {s.post.media && s.post.media[0] ? (
                        <>
                          <img
                            src={`${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(s.post.media[0].thumbnailPath || s.post.media[0].filePath)}&w=200&token=${token}`}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                          />
                          <span className="relative z-10 drop-shadow-lg">#{idx + 1}</span>
                        </>
                      ) : (
                        `#${idx + 1}`
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-foreground font-bold truncate group-hover:text-primary transition-colors">
                          {getPostText(s.post)}
                        </p>
                        {s.isBanger && <Flame className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex items-center gap-1 text-[10px] font-black text-muted-foreground">
                          <Heart className="h-3 w-3 text-red-500/50" /> {s.likes}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] font-black text-muted-foreground">
                          <Eye className="h-3 w-3 text-primary/50" /> {s.views}
                        </div>
                        {getPlatformIcon(s.socialAccount?.platform || 'unknown')}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Scheduled & History (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Scheduled Posts */}
          <Card className="bg-card border-border shadow-xl overflow-hidden">
            <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
                  <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                    <Clock className="h-4 w-4 text-primary" />
                  </div>
                  Scheduled Posts
                </CardTitle>
                <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">Upcoming publications</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[400px]">
                <Table>
                  <TableHeader className="bg-muted/80 sticky top-0 z-10">
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">When</TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px] w-16">Media</TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Content Preview</TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Platform</TableHead>
                      <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingScheduled ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                    ) : scheduledPosts.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground text-sm font-medium">No posts scheduled for the future.</TableCell></TableRow>
                    ) : (
                      scheduledPosts.map((s) => (
                        <TableRow key={s.id} className="border-border hover:bg-card/30 transition-colors">
                          <TableCell className="text-[10px] font-black uppercase">
                            <div className="flex flex-col">
                              <span className={cn(
                                s.status === 'PROCESSING' ? "text-primary animate-pulse" : "text-amber-500"
                              )}>
                                {moment(s.scheduledAt).format('MMM DD')}
                              </span>
                              <span className="text-muted-foreground">{moment(s.scheduledAt).format('HH:mm')}</span>
                              {s.status === 'PROCESSING' && (
                                <span className="text-[7px] text-primary font-black mt-0.5 tracking-tighter flex items-center gap-1">
                                  <Loader2 className="h-2 w-2 animate-spin" />
                                  PROCESSING...
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {s.post.media && s.post.media[0] && (
                              <div 
                                className="h-10 w-10 rounded-lg overflow-hidden bg-card border border-border cursor-pointer hover:border-primary/50 transition-colors"
                                onClick={() => openPostById(s.post.id)}
                              >
                                <img
                                  src={`${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(s.post.media[0].thumbnailPath || s.post.media[0].filePath)}&w=200&token=${token}`}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            <p className="text-xs text-foreground font-medium line-clamp-2 leading-relaxed" onClick={() => openPostById(s.post.id)}>
                              {getPostText(s.post) || 'No content'}
                            </p>
                            <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-wider">{s.post.game.name}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {getPlatformIcon(s.socialAccount?.platform || 'unknown')}
                              <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[100px]">{s.socialAccount?.handle ? (s.socialAccount.handle.startsWith('@') ? s.socialAccount.handle : `@${s.socialAccount.handle}`) : 'unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                onClick={() => sendNowMutation.mutate(s.id)}
                                disabled={sendNowMutation.isPending}
                                title="Send Now"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this scheduled post?')) {
                                    cancelScheduleMutation.mutate(s.id);
                                  }
                                }}
                                disabled={cancelScheduleMutation.isPending}
                                title="Cancel Schedule"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* All Posts Table */}
          <Card className="bg-card border-border shadow-xl overflow-hidden">
            <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
                  <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </div>
                  Publication History
                </CardTitle>
                <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">All sent posts & engagement</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader className="bg-muted/80 sticky top-0 z-10">
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Date</TableHead>
                      <TableHead className="w-6 p-0"></TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px] w-16">Media</TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Content</TableHead>
                      <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Aggregate Engagement</TableHead>
                      <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Platforms</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingAll ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                    ) : allPosts.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground text-sm font-medium">No publication history found.</TableCell></TableRow>
                    ) : (
                      allPosts.map((post) => {
                        const allDeleted = post.schedules.length > 0 && post.schedules.every(s => s.status === 'DELETED');
                        const allFailed = post.schedules.length > 0 && post.schedules.every(s => s.status === 'FAILED' || s.status === 'DELETED') && post.schedules.some(s => s.status === 'FAILED');
                        const isAnyPublishing = post.platforms?.some((p: any) => 
                          p.status === 'PROCESSING' || 
                          (p.status === 'PENDING' && 
                           new Date(p.scheduledAt).getTime() <= Date.now() && 
                           new Date(p.scheduledAt).getTime() > Date.now() - (5 * 60 * 1000))
                        );

                        return (
                          <TableRow 
                            key={post.id} 
                            className={cn(
                              "border-border transition-colors cursor-pointer group/row",
                              allDeleted ? "opacity-25 grayscale hover:bg-rose-950/10" : 
                              allFailed ? "bg-red-950/10 hover:bg-red-950/20 border-red-900/50" :
                              "hover:bg-card/30"
                            )}
                            onClick={() => openPostById(post.id)}
                          >
                          <TableCell className={cn(
                            "text-[10px] font-bold uppercase",
                            allDeleted ? "text-muted-foreground" : (allFailed ? "text-red-500" : (isAnyPublishing ? "text-primary" : "text-muted-foreground"))
                          )}>
                            <div className="flex flex-col">
                              <span className={cn(
                                allDeleted && "line-through decoration-rose-500/50",
                                isAnyPublishing && "animate-pulse"
                              )}>
                                {moment(post.createdAt).format('MMM DD, YYYY')}
                              </span>
                              {isAnyPublishing && (
                                <span className="text-[7px] text-primary font-black mt-0.5 tracking-tighter flex items-center gap-1">
                                  <Loader2 className="h-2 w-2 animate-spin" />
                                  PROCESSING...
                                </span>
                              )}
                              {allFailed && !allDeleted && <span className="text-[7px] text-red-500 font-black mt-0.5 tracking-tighter uppercase">Publication Failed</span>}
                              {allDeleted && <span className="text-[7px] text-rose-500/70 font-black mt-0.5 tracking-tighter">PERMANENTLY DELETED</span>}
                            </div>
                          </TableCell>
                          <TableCell className="w-6 p-0">
                            <div className="flex flex-col gap-1 items-center">
                              {post.schedules.some(s => s.isBanger) && !allDeleted && (
                                <Flame className="h-4 w-4 text-amber-500 fill-amber-500 animate-pulse" />
                              )}
                              {post.schedules.some(s => s.status === 'DELETED') && (
                                <div title={allDeleted ? "All posts have been deleted from social media" : "One or more posts have been deleted from social media"}>
                                  <Trash2 className={cn("h-3 w-3", allDeleted ? "text-rose-400" : "text-rose-500")} />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const firstEmbeddable = post.platforms.find(p => 
                                (p.platform?.toLowerCase().includes('youtube') || 
                                 p.platform?.toLowerCase().includes('tiktok') || 
                                 p.platform?.toLowerCase().includes('bluesky')) && 
                                p.externalPostId && 
                                p.status === 'COMPLETED'
                              );

                              const hasLocalMedia = post.media && post.media[0] && !post.media[0].filePath.includes('deleted');

                              if (!hasLocalMedia && firstEmbeddable) {
                                return (
                                  <div className="h-10 w-10 rounded-lg overflow-hidden bg-black border border-border flex items-center justify-center">
                                    {getPlatformIconUtil(firstEmbeddable.platform.toLowerCase().replace('x (twitter)', 'twitter'), 'h-5 w-5')}
                                  </div>
                                );
                              }

                              if (post.media && post.media[0]) {
                                return (
                                  <div className={cn(
                                    "h-10 w-10 rounded-lg overflow-hidden bg-card border border-border shadow-sm transition-colors relative",
                                    allDeleted ? "border-rose-500/20" : "group-hover/row:border-primary/50"
                                  )}>
                                    {isAnyPublishing && (
                                      <div className="absolute inset-0 bg-primary/40 backdrop-blur-[1px] flex items-center justify-center z-10 animate-in fade-in duration-300">
                                        <Loader2 className="h-5 w-5 text-white animate-spin" />
                                      </div>
                                    )}
                                    {post.media[0].mediaType === 'video' ? (
                                      <img
                                        src={`${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(post.media[0].thumbnailPath || post.media[0].filePath)}&w=200&token=${token}`}
                                        alt=""
                                        className={cn("h-full w-full object-cover", allDeleted && "sepia")}
                                      />
                                    ) : (
                                      <img
                                        src={`${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(post.media[0].filePath)}&w=200&token=${token}`}
                                        alt=""
                                        className={cn("h-full w-full object-cover", allDeleted && "sepia")}
                                      />
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </TableCell>
                          <TableCell className="max-w-[250px]">
                            <div className="flex items-center gap-2">
                              <p 
                                className={cn(
                                  "text-xs font-medium line-clamp-2 transition-colors",
                                  allDeleted ? "text-muted-foreground line-through decoration-rose-500/30" : "text-foreground group-hover/row:text-primary"
                                )}
                              >
                                {getPostText(post) || 'No content'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={cn("grid grid-cols-2 gap-x-4 gap-y-1 transition-opacity", allDeleted ? "opacity-20" : "opacity-100")}>
                              <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground">
                                <Heart className="h-2.5 w-2.5 text-red-500/40" /> {post.totalLikes}
                              </div>
                              <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground">
                                <Eye className="h-2.5 w-2.5 text-primary/40" /> {post.totalViews}
                              </div>
                              <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground">
                                <MessageSquare className="h-2.5 w-2.5 text-primary/40" /> {post.totalComments}
                              </div>
                              <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground">
                                <Share2 className="h-2.5 w-2.5 text-green-500/40" /> {post.totalShares}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end">
                              <PublishStatusBadge platforms={post.platforms} />
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
